import { useMemo, useState } from 'react';
import axios from 'axios';
import DriverBrandHeader from './DriverBrandHeader';
import { API_BASE_URL } from '../../apiConfig';
import { getDriverLanguage } from './driverI18n';
import { getDriverFeatureText, getLocalizedDriverApiError } from './driverFeatureI18n';

const readDocument = (file) => new Promise((resolve, reject) => {
  if (!file) return resolve({});
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => resolve({ name: file.name, content: reader.result, mime_type: file.type });
  reader.readAsDataURL(file);
});

export default function DriverRegistration({ onBack, onCompleted }) {
  const [mode, setMode] = useState('driver');
  const [driver, setDriver] = useState({ name: '', phone: '', password: '', fleet_code: '' });
  const [fleet, setFleet] = useState({ fleet_name: '', contact_name: '', phone: '', password: '', email: '', address: '' });
  const [registrationFile, setRegistrationFile] = useState(null);
  const [permitFile, setPermitFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const language = getDriverLanguage();
  const copy = getDriverFeatureText(language).registration;
  const title = useMemo(() => mode === 'fleet' ? copy.fleetTitle : copy.driverTitle, [copy.driverTitle, copy.fleetTitle, mode]);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setSubmitting(true);
    try {
      if (mode === 'driver') {
        await axios.post(`${API_BASE_URL}/fleet/register-driver`, driver);
        setMessage(copy.driverSuccess);
      } else {
        const [business_registration, transport_permit] = await Promise.all([readDocument(registrationFile), readDocument(permitFile)]);
        await axios.post(`${API_BASE_URL}/fleet/register`, { ...fleet, documents: { business_registration, transport_permit } });
        setMessage(copy.fleetSuccess);
      }
      onCompleted?.();
    } catch (requestError) {
      setError(getLocalizedDriverApiError(requestError, copy.submitFailed, language));
    } finally {
      setSubmitting(false);
    }
  };

  const updateDriver = (field, value) => setDriver((current) => ({ ...current, [field]: value }));
  const updateFleet = (field, value) => setFleet((current) => ({ ...current, [field]: value }));

  return (
    <div
      className="driver-luxury-portal driver-registration-screen min-h-screen bg-surface"
      data-testid="driver-registration-scroll-container"
    >
      <DriverBrandHeader pageTitle={copy.accountRegistration} />
      <main className="mx-auto w-full max-w-md px-4 py-6 pb-12">
        <button type="button" onClick={onBack} className="mb-5 text-sm text-muted-foreground">‹ {copy.back}</button>
        <section className="premium-card p-5">
          <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-muted p-1">
            <button type="button" onClick={() => setMode('driver')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${mode === 'driver' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>{copy.driverTab}</button>
            <button type="button" onClick={() => setMode('fleet')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${mode === 'fleet' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>{copy.fleetTab}</button>
          </div>
          <h1 className="text-xl font-bold text-foreground">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === 'fleet' ? copy.fleetHint : copy.driverHint}
          </p>
          {error && <div className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
          {message && <div className="mt-4 rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700">{message}</div>}
          <form className="mt-5 space-y-4" onSubmit={submit}>
            {mode === 'driver' ? (
              <>
                <Field label={copy.name}><input className="premium-input" required value={driver.name} onChange={(event) => updateDriver('name', event.target.value)} /></Field>
                <Field label={copy.phone}><input className="premium-input" type="tel" required value={driver.phone} onChange={(event) => updateDriver('phone', event.target.value)} /></Field>
                <Field label={copy.password}><input className="premium-input" type="password" minLength="6" required value={driver.password} onChange={(event) => updateDriver('password', event.target.value)} /></Field>
                <Field label={copy.fleetCode}><input className="premium-input" value={driver.fleet_code} onChange={(event) => updateDriver('fleet_code', event.target.value.toUpperCase())} placeholder={copy.fleetCodePlaceholder} /></Field>
              </>
            ) : (
              <>
                <Field label={copy.fleetName}><input className="premium-input" required value={fleet.fleet_name} onChange={(event) => updateFleet('fleet_name', event.target.value)} /></Field>
                <Field label={copy.contactName}><input className="premium-input" required value={fleet.contact_name} onChange={(event) => updateFleet('contact_name', event.target.value)} /></Field>
                <Field label={copy.contactPhone}><input className="premium-input" type="tel" required value={fleet.phone} onChange={(event) => updateFleet('phone', event.target.value)} /></Field>
                <Field label={copy.loginPassword}><input className="premium-input" type="password" minLength="6" required value={fleet.password} onChange={(event) => updateFleet('password', event.target.value)} /></Field>
                <Field label={copy.email}><input className="premium-input" type="email" value={fleet.email} onChange={(event) => updateFleet('email', event.target.value)} /></Field>
                <Field label={copy.companyAddress}><input className="premium-input" value={fleet.address} onChange={(event) => updateFleet('address', event.target.value)} /></Field>
                <Field label={copy.businessRegistration}><input className="premium-input" type="file" required onChange={(event) => setRegistrationFile(event.target.files?.[0] || null)} /></Field>
                <Field label={copy.transportPermit}><input className="premium-input" type="file" required onChange={(event) => setPermitFile(event.target.files?.[0] || null)} /></Field>
              </>
            )}
            <button disabled={submitting} className="premium-button w-full bg-primary py-3 text-primary-foreground disabled:opacity-50">
              {submitting ? copy.submitting : copy.submit}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block text-sm font-medium text-foreground"><span className="mb-1 block">{label}</span>{children}</label>;
}
